/* eslint-disable max-lines */
import React, { FC, useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { EnvelopeDc } from "@evergis/api";
import { useMapViewActions } from "@evergis/react";
import { useTranslation } from "react-i18next";

import {
  Checkbox,
  Description,
  Divider,
  FlexSpan,
  Icon,
  IconButton,
  IconToggle,
  IconToggleButton,
  LinearProgress,
  Table,
  Tooltip,
} from "@evergis/ui";

import {
  DeleteFeauturesConfirmContainer,
  IconButtonChip,
  IconWithChip,
  PlaceholderContainer,
  TableContainer,
  TableContainerControls,
  TableContainerHeader,
  TableContainerTitle,
  TableContainerTitleText,
  TableDropdownContainer,
  TableTitleAndTools,
  TempLayerChip,
} from "../styled";
import { ErrorMessage } from "../../../components/Dialogs/NewLayer/components/ErrorMessage";
import { TableHeightDrag } from "./TableHeightDrag";
import { CopyIcon } from "./CopyIcon";
import { ExportIcon } from "./ExportIcon";

import { layerExtent } from "../../../resources/layers";

import { closeBottomContainer, expandBottomContainer, openBottomContainer } from "../../../ducks/layout/layout";
import { getSelectedFeaturesTotalCounts } from "../../../ducks/selectFeatures/selectors";
import { useAction } from "../../../hooks/useAction";
import { useConfirmDialog, useFeaturesMultiEditDialog } from "../../../hooks/useDialogs";
import { isEditable } from "../../../evergis/core/permissions";
import { useTableMenu } from "../hooks/useTableMenu";
import { useTableHeight } from "../hooks/useTableHeight";
import { useTableData } from "../hooks/useTableData";
import { TABLE_PAGE_LIMIT, useTableProps } from "../hooks/useTableProps";
import { usePostgresLayer } from "../../../hooks/layer/usePostgresLayer";
import { goToMainBase } from "../../../ducks/location/location";
import { useProjectName } from "../../../hooks/project/useProjectName";
import { useTableActiveLayers } from "../hooks/useTableActiveLayers";
import { useTableSelection } from "../../../hooks/table/useTableSelection";
import { TableLayersList } from "./TableLayersList";
import { useDeleteFeatures } from "../hooks/useDeleteFeatures";
import { useTableSelectedCount } from "../../../hooks/table/useTableSelectedCount";
import { useTempLayerConditions } from "../../../hooks/layer/useTempLayerConditions";
import { useTableSelectionCondition } from "../../../hooks/table/useTableSelectionCondition";
import { useProjectSrid } from "../../../ducks/project/hooks";
import { useGeometrySelectionConditions } from "../../../hooks/geometrySelection/useGeometrySelectionConditions";
import { resetFeatureToShow } from "../../../ducks/showFeature/showFeature";
import { useLayerConditionCount } from "../../../hooks/layer/useLayerConditionCount";
import { useTempLayer } from "../../../hooks/layer/useTempLayer";
import { isWrite } from "../../../utils/permissions";
import { setLayerTempFilterTool } from "../../../ducks/layerTempFilter/layerTempFilter";
import { isLocationEqlQuerySelector, isLocationMainSelector } from "../../../ducks/location/selectors";
import { useTableLayerId } from "../../../hooks/table/useTableLayerId";
import { useGeometrySelection } from "../../../hooks/geometrySelection/useGeometrySelection";
import { useIsDashboard } from "../../../hooks/location/useIsDashboard";
import { useSelectFeaturesGeometryMasking } from "../../../hooks/selectFeatures/useSelectFeaturesGeometryMasking";
import { useIsShared } from "../../../hooks/api/useIsShared";
import { useMapLegendTableResizing } from "../hooks/useMapLegendTableResizing";

import { ConfirmDialogOptions } from "../../../components/Dialogs/Confirmation/Confirmation";
import { FilterConditionOperation } from "../../../components/LayerFilter/types";
import { TableSelectionMode } from "../../../ducks/table/types";
import { RoutePanelName } from "../../../appConfig/types";

// eslint-disable-next-line max-lines-per-function
export const EvergisTable: FC = () => {
  const { t } = useTranslation("table");
  const dispatch = useDispatch();
  const [currentLayer, setTableLayerId] = useTableLayerId();
  const maximizeTable = useAction(expandBottomContainer);
  const minimizeTable = useAction(openBottomContainer);
  const closeTable = useAction(closeBottomContainer);
  const { postgresLayer, loading: layerInfoLoading } = usePostgresLayer(currentLayer, { waitInput: true });
  const { tempLayerSourceName, getTempLayerInfo, tempLayerError } = useTempLayer();
  const tempLayerInfo = getTempLayerInfo(currentLayer);
  const layer = tempLayerInfo || postgresLayer;
  const isLayerEditable = isEditable(layer.permissions);
  const isProjectLocation = useSelector(isLocationMainSelector);
  const isEqlQueryLocation = useSelector(isLocationEqlQuerySelector);
  const isLayerFiltered = !!layer.condition;
  const layerConditionsCount = useLayerConditionCount(layer);
  const tableHeight = useTableHeight();
  const { isTableFullscreen, isResizing, setIsResizing } = useMapLegendTableResizing();
  const confirmDialog = useConfirmDialog();
  const openFeaturesMultiEditDialog = useFeaturesMultiEditDialog();
  const projectName = useProjectName();
  const geometrySelectionConditions = useGeometrySelectionConditions();
  const tableActiveLayers = useTableActiveLayers();
  const [tableSelection, setTableSelection] = useTableSelection();
  const deleteFeatures = useDeleteFeatures(layer);
  const { selectedCount, isSelectedAll, isSelectedSome, isSelectionAll } = useTableSelectedCount(currentLayer);
  const selectConditions = useTableSelectionCondition(layer);
  const tempConditions = useTempLayerConditions(false);
  const spatialReference = useProjectSrid();
  const { animateTo } = useMapViewActions();
  const [getLayerExtent, { data: extentLayer, loading }] = layerExtent.use();
  const [{ history, loading: geometrySelectionLoading }] = useGeometrySelection();
  const [selectFeaturesGeometryMasking, setSelectFeaturesGeometryMasking] = useSelectFeaturesGeometryMasking();
  const totalCounts = useSelector(getSelectedFeaturesTotalCounts);
  const isDashboard = useIsDashboard();
  const isShared = useIsShared();

  const {
    processedLayer,
    tableColumns,
    columnParams,
    updateColumnParams,
    queryParams,
    sort,
    changeSort,
    handleToggleSort,
    currentPage,
    onPageChange,
    isFiltering,
    toggleFiltering,
    applyFilterToLayer,
    isFilterApplyingToLayer,
    clearFilters,
    purgeTable,
  } = useTableProps(layer);

  const { tableData, totalRowsCount, tableDataLoading, tableDataRefetch, tableDataError } = useTableData(
    layer,
    currentPage,
    queryParams,
    sort?.[currentLayer] ?? {},
    isFiltering,
  );

  const { onShowCard } = useTableMenu(currentLayer);

  const zoomTo = useCallback(async () => {
    await getLayerExtent({
      input: {
        name: currentLayer,
        sr: spatialReference,
        condition: [tempConditions[currentLayer], selectConditions]
          .filter(Boolean)
          .join(` ${FilterConditionOperation.AND} `),
      },
    });
  }, [currentLayer, getLayerExtent, selectConditions, spatialReference, tempConditions]);

  const onTableClose = useCallback(() => {
    if (isProjectLocation) {
      dispatch(
        goToMainBase({
          project: projectName,
          sharedMap: isShared ? projectName : undefined,
          panel: RoutePanelName.Layers,
        }),
      );
    }

    closeTable();
    purgeTable();
  }, [closeTable, dispatch, isProjectLocation, projectName, purgeTable, isShared]);

  // eslint-disable-next-line max-lines-per-function
  const renderTools = useMemo(() => {
    return (
      <>
        <IconButton title={t("zoomToObjects")} kind="zoom_to" onClick={zoomTo} disabled={tableDataLoading} />
        {isSelectionAll || isSelectedSome ? (
          <>
            {!isShared && (
              <>
                <ExportIcon name={layer.name} alias={layer.alias} />
                <CopyIcon name={currentLayer} />
              </>
            )}
            {isWrite(layer.permissions) && (
              <IconButton
                title={t("delete")}
                kind="delete"
                onClick={() => {
                  confirmDialog([""], {
                    title: t("objectsDeletion"),
                    content: (
                      <DeleteFeauturesConfirmContainer>
                        <div>
                          {`${t("objectsDeletionConfirm1")} (${selectedCount}) ${t("fromLayer")}`}{" "}
                          <strong>{layer.alias || layer.name}</strong>?
                        </div>
                        <br />
                        <div>{t("objectsDeletionConfirm2")}.</div>
                      </DeleteFeauturesConfirmContainer>
                    ),
                    onConfirm: () => {
                      deleteFeatures().then(() => {
                        setTableSelection(undefined);
                        dispatch(resetFeatureToShow());
                      });
                    },
                    options: {
                      maxWidth: "40rem",
                    },
                  } as ConfirmDialogOptions);
                }}
              />
            )}
          </>
        ) : (
          <>
            {isLayerEditable && (
              <>
                <IconButton
                  title={t("calculate")}
                  kind="calculation"
                  onClick={() => openFeaturesMultiEditDialog([currentLayer])}
                  disabled={tableDataLoading}
                />
                <IconWithChip>
                  <IconButton
                    title={t("layerFilters")}
                    kind="layer_filter"
                    onClick={() =>
                      dispatch(
                        setLayerTempFilterTool({
                          layerName: currentLayer,
                          active: true,
                        }),
                      )
                    }
                    disabled={tableDataLoading}
                    primary={isLayerFiltered}
                  />
                  {isLayerFiltered && <IconButtonChip text={layerConditionsCount} secondary />}
                </IconWithChip>
                <Divider vertical style={{ margin: "0 0.5rem" }} />
              </>
            )}
            <IconToggle
              title={t("tableFilters")}
              kind="attribute_filter"
              onClick={toggleFiltering}
              isSelected={isFiltering}
              disabled={tableDataLoading}
            />
            {(isFiltering || !!geometrySelectionConditions?.[currentLayer]) && isLayerEditable && (
              <IconButton
                title={t("applyFilterToLayer")}
                kind="save"
                onClick={() =>
                  confirmDialog([""], {
                    content: `${t("filterApplyConfirm")} ${layer.alias || layer.name}`,
                    onConfirm: applyFilterToLayer,
                    options: {
                      maxWidth: "40rem",
                    },
                  } as ConfirmDialogOptions)
                }
                disabled={
                  tableDataLoading ||
                  (!queryParams.query && !geometrySelectionConditions?.[currentLayer]) ||
                  isFilterApplyingToLayer
                }
              />
            )}
            {isFiltering && (
              <IconButton
                title={t("clearFilters")}
                kind="clear"
                onClick={clearFilters}
                disabled={tableDataLoading || !queryParams.query}
              />
            )}
            {history.features.length > 0 && history.step > -1 && (
              <>
                <Divider vertical style={{ margin: "0 0.5rem" }} />
                <IconToggleButton
                  isSelected={selectFeaturesGeometryMasking}
                  onClick={() => setSelectFeaturesGeometryMasking(!selectFeaturesGeometryMasking)}
                >
                  <Icon kind="preview" />
                </IconToggleButton>
              </>
            )}
          </>
        )}
      </>
    );
  }, [
    isShared,
    applyFilterToLayer,
    clearFilters,
    confirmDialog,
    currentLayer,
    deleteFeatures,
    dispatch,
    geometrySelectionConditions,
    isFilterApplyingToLayer,
    isFiltering,
    isLayerEditable,
    isLayerFiltered,
    isSelectedSome,
    isSelectionAll,
    layerConditionsCount,
    openFeaturesMultiEditDialog,
    layer.alias,
    layer.name,
    layer.permissions,
    queryParams.query,
    selectedCount,
    setTableSelection,
    t,
    tableDataLoading,
    toggleFiltering,
    zoomTo,
    setSelectFeaturesGeometryMasking,
    selectFeaturesGeometryMasking,
    history.features,
    history.step,
  ]);

  useEffect(() => {
    if (!tableActiveLayers.length || tableActiveLayers.some(item => item.name === currentLayer)) return;

    if (isEqlQueryLocation || isDashboard) return;

    const layerName = tableActiveLayers[0].name;

    if (isProjectLocation && !isShared) {
      dispatch(
        goToMainBase({
          project: projectName,
          sharedMap: isShared ? projectName : undefined,
          panel: RoutePanelName.Table,
          layer: layerName,
          query: { layerName },
        }),
      );
    }

    setTableLayerId(layerName);
  }, [currentLayer, tableActiveLayers]); // eslint-disable-line

  useEffect(() => {
    if (extentLayer && !loading) {
      const {
        coordinates: [[xMin, yMin], [xMax, yMax]],
      } = extentLayer as EnvelopeDc;

      if (xMin || yMin || xMax || yMax) {
        animateTo({
          extent: { xMax, xMin, yMax, yMin },
        });
      }
    }
  }, [extentLayer, loading]); // eslint-disable-line

  useEffect(() => {
    return () => {
      purgeTable();
      closeTable();
    };
  }, []); // eslint-disable-line

  if (!currentLayer) return null;

  if (!tempLayerError && (layerInfoLoading || processedLayer.current !== currentLayer)) {
    return <LinearProgress />;
  }

  return (
    <TableContainer>
      <TableHeightDrag setIsResizing={setIsResizing} />
      <TableContainerHeader>
        <TableTitleAndTools>
          {!layerInfoLoading ? (
            <>
              <TableContainerTitle>
                <TableContainerTitleText>
                  {isSelectionAll || isSelectedSome ? (
                    <>
                      {isSelectedAll
                        ? `${t("allObjectsSelected")} (${totalRowsCount})`
                        : `${t("selectedObjects")}: ${selectedCount} ${t("of")} ${totalRowsCount}`}
                    </>
                  ) : (
                    <TableDropdownContainer>
                      <TableLayersList currentLayer={currentLayer} />
                      {tempLayerSourceName === currentLayer && <TempLayerChip />}
                    </TableDropdownContainer>
                  )}
                </TableContainerTitleText>
              </TableContainerTitle>
              {isProjectLocation && renderTools}
            </>
          ) : null}
        </TableTitleAndTools>
        <TableContainerControls>
          <IconButton
            title={t("refresh")}
            kind="refresh"
            disabled={tableDataLoading}
            onClick={async () => {
              changeSort({});
              await tableDataRefetch();
            }}
          />
          {isProjectLocation && (
            <>
              {!isTableFullscreen ? (
                <IconButton
                  title={t("fullscreen")}
                  kind="maximize_solid"
                  onClick={() => {
                    maximizeTable();
                  }}
                />
              ) : (
                <IconButton title={t("exitFullscreen")} kind="minimize_solid" onClick={minimizeTable} />
              )}
            </>
          )}
          {(isProjectLocation || isEqlQueryLocation) && (
            <IconButton title={t("close")} kind="close" onClick={onTableClose} />
          )}
        </TableContainerControls>
      </TableContainerHeader>
      {(tempLayerError || tableDataError) && <ErrorMessage error={tempLayerError || tableDataError} />}
      {!(tempLayerError || tableDataError) && (
        <Table
          columns={tableColumns}
          data={isResizing ? [] : tableData.current}
          sort={sort?.[currentLayer] ?? {}}
          onToggleSort={handleToggleSort}
          height={tableHeight}
          filtersOn={isFiltering}
          totalRowsCount={totalRowsCount}
          pageSize={TABLE_PAGE_LIMIT}
          onPageChange={onPageChange}
          currentPage={currentPage}
          loading={layerInfoLoading || tableDataLoading}
          paginationDisabled={layerInfoLoading || tableDataLoading}
          onColumnResize={updateColumnParams}
          columnParams={columnParams}
          totalText={t("total")}
          placeholder={
            totalCounts?.[currentLayer] === 0 &&
            !geometrySelectionLoading &&
            history.features?.[history.step]?.length > 0 &&
            history.step > -1 && (
              <PlaceholderContainer>
                <FlexSpan>
                  <Icon kind="points" />
                </FlexSpan>
                <FlexSpan>
                  <Description>{t("noObjectFound")}</Description>
                </FlexSpan>
              </PlaceholderContainer>
            )
          }
          special={{
            width: isProjectLocation ? 82 : null,
            header: isProjectLocation && (
              <Checkbox
                checked={isSelectionAll}
                onChange={e =>
                  setTableSelection({
                    ...tableSelection,
                    [currentLayer]: {
                      mode: e.target.checked ? TableSelectionMode.All : TableSelectionMode.None,
                      processedRows: [],
                      totalRows: totalRowsCount,
                    },
                  })
                }
              />
            ),
            render: row => (
              <>
                <Tooltip content={t("showFeatureCard")} placement="top" arrow>
                  {ref => <IconButton kind={"feature_card"} innerRef={ref} onClick={() => onShowCard(row)} />}
                </Tooltip>
                {isProjectLocation && (
                  <Checkbox
                    checked={
                      isSelectionAll
                        ? !tableSelection?.[currentLayer]?.processedRows.includes(row.id)
                        : tableSelection?.[currentLayer]?.processedRows.includes(row.id)
                    }
                    onChange={e => {
                      const currentProcessedRows = tableSelection?.[currentLayer]?.processedRows ?? [];
                      const processedRows = e.target.checked
                        ? isSelectionAll
                          ? currentProcessedRows.filter(item => item !== row.id)
                          : [...currentProcessedRows, row.id]
                        : isSelectionAll
                        ? [...currentProcessedRows, row.id]
                        : currentProcessedRows.filter(item => item !== row.id);

                      setTableSelection({
                        ...tableSelection,
                        [currentLayer]: {
                          mode: tableSelection?.[currentLayer]?.mode ?? TableSelectionMode.None,
                          processedRows,
                          totalRows: totalRowsCount,
                        },
                      });
                    }}
                  />
                )}
              </>
            ),
          }}
        />
      )}
    </TableContainer>
  );
};
